import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { approvalsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Loader2, CheckCircle2, XCircle, AlertCircle, Clock, MessageSquare } from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, color: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved: { label: 'Approved', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'bg-red-100 text-red-800 border-red-200' },
  changes_requested: { label: 'Changes Requested', icon: MessageSquare, color: 'bg-purple-100 text-purple-800 border-purple-200' },
};

const ENTITY_TYPES = ['general', 'file', 'design', 'invoice', 'material'];

export default function ProjectApprovals({ projectId }) {
  const { isAdmin, isStaff, isClient, user } = useAuth();
  const canCreate = isAdmin || isStaff;
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [respondOpen, setRespondOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [form, setForm] = useState({ title: '', description: '', entity_type: 'general' });
  const [responseData, setResponseData] = useState({ action: '', note: '' });

  const fetchApprovals = useCallback(async () => {
    try {
      const res = await approvalsAPI.list(projectId);
      setApprovals(res.data);
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchApprovals(); }, [fetchApprovals]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await approvalsAPI.create(projectId, form);
      setCreateOpen(false);
      setForm({ title: '', description: '', entity_type: 'general' });
      fetchApprovals();
    } catch (err) {
      console.error('Failed to create approval:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    if (!selectedApproval || !responseData.action) return;
    setFormLoading(true);
    try {
      await approvalsAPI.respond(selectedApproval.approval_id, responseData);
      setRespondOpen(false);
      setSelectedApproval(null);
      setResponseData({ action: '', note: '' });
      fetchApprovals();
    } catch (err) {
      console.error('Failed to respond:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const pending = approvals.filter(a => a.status === 'pending');
  const resolved = approvals.filter(a => a.status !== 'pending');

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  return (
    <div className="space-y-6" data-testid="project-approvals">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Approvals ({approvals.length})</h3>
        {canCreate && (
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="request-approval-btn">
            <Plus className="h-4 w-4 mr-1" /> Request Approval
          </Button>
        )}
      </div>

      {/* Pending */}
      {pending.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-amber-700">Pending ({pending.length})</h4>
          {pending.map(apr => {
            const cfg = STATUS_CONFIG[apr.status];
            const Icon = cfg.icon;
            return (
              <Card key={apr.approval_id} className="border-amber-200" data-testid={`approval-${apr.approval_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${cfg.color}`}><Icon className="h-4 w-4" /></div>
                      <div>
                        <p className="font-medium">{apr.title}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{apr.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Requested by {apr.requested_by_name} on {formatDate(apr.created_at)}
                          <span className="ml-2 capitalize text-xs px-1.5 py-0.5 rounded bg-muted">{apr.entity_type}</span>
                        </p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => { setSelectedApproval(apr); setResponseData({ action: '', note: '' }); setRespondOpen(true); }} data-testid={`respond-approval-${apr.approval_id}`}>
                      Respond
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Resolved */}
      {resolved.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">History ({resolved.length})</h4>
          {resolved.map(apr => {
            const cfg = STATUS_CONFIG[apr.status];
            const Icon = cfg.icon;
            return (
              <Card key={apr.approval_id} className="opacity-80" data-testid={`approval-${apr.approval_id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-1.5 rounded-full ${cfg.color}`}><Icon className="h-3.5 w-3.5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{apr.title}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                      </div>
                      {apr.response_note && <p className="text-xs text-muted-foreground mt-0.5">"{apr.response_note}"</p>}
                      <p className="text-xs text-muted-foreground">{apr.responded_by_name} — {formatDate(apr.responded_at)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {approvals.length === 0 && (
        <Card><CardContent className="py-10 text-center text-muted-foreground"><AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No approval requests yet</p></CardContent></Card>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Approval</DialogTitle>
            <DialogDescription>Send an item for client review and approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required placeholder="e.g., Kitchen tile selection" data-testid="approval-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.entity_type} onValueChange={v => setForm({...form, entity_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map(t => <SelectItem key={t} value={t}><span className="capitalize">{t}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Details about what needs approval..." rows={3} data-testid="approval-desc-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="submit-approval-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Send for Approval
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Respond Dialog */}
      <Dialog open={respondOpen} onOpenChange={setRespondOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Respond to Approval</DialogTitle>
            <DialogDescription>"{selectedApproval?.title}"</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRespond}>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">{selectedApproval?.description}</p>
              <div className="space-y-2">
                <Label>Your Decision</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'approved', label: 'Approve', style: 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
                    { value: 'changes_requested', label: 'Request Changes', style: 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100' },
                    { value: 'rejected', label: 'Reject', style: 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100' },
                  ].map(opt => (
                    <button key={opt.value} type="button"
                      className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                        responseData.action === opt.value ? opt.style + ' ring-2 ring-offset-1 ring-current' : 'border-border bg-background hover:bg-muted'
                      }`}
                      onClick={() => setResponseData({...responseData, action: opt.value})}
                      data-testid={`approval-action-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note (optional)</Label>
                <Textarea value={responseData.note} onChange={e => setResponseData({...responseData, note: e.target.value})} placeholder="Add a note..." rows={2} data-testid="approval-response-note" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRespondOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading || !responseData.action} data-testid="submit-response-btn">
                {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Submit
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
